import { cloneElement, isValidElement, ReactElement, ReactNode } from 'react'

import { FEATURE } from 'constants/feature'
import { usePermission } from 'hooks/use-permission'
import { PermissionAction } from 'permissions/check-permission'

export type CanDeniedBehavior = 'hide' | 'disable'

export type CanProps = {
  feature?: FEATURE
  action?: PermissionAction
  /**
   * Extra grant OR-ed with the role check — e.g. contract manager or multisig
   * proposer. Capability sources are page-scoped, so the caller resolves them.
   */
  or?: boolean
  /** When access is denied: remove UI (`hide`) or show disabled (`disable`). Default: hide. */
  denied?: CanDeniedBehavior
  fallback?: ReactNode
  children: ReactNode
}

const Can = ({
  feature,
  action = 'view',
  or,
  denied = 'hide',
  fallback = null,
  children,
}: CanProps) => {
  const { canView, canCRUD } = usePermission()

  const roleOk = feature ? (action === 'crud' ? canCRUD(feature) : canView(feature)) : false
  const allowed = roleOk || Boolean(or)

  if (allowed) {
    return <>{children}</>
  }

  if (denied === 'disable' && isValidElement(children)) {
    const child = children as ReactElement<{ disabled?: boolean }>
    return cloneElement(child, {
      disabled: Boolean(child.props.disabled) || true,
    })
  }

  return <>{fallback}</>
}

export default Can
